import { IntervalResource } from '../../typings/Interval';
import { PaginatedResource } from '../../typings/Paginated';
import { AlreadyExists, BaseService, ClientError, InvalidValue, NotFound, Unauthorized, UnexpectedError } from './base-service';

export enum CitizenStatus {
    'ACTIVE' = 'active',
    'DISABLED' = 'disabled',
    'WAITING' = 'waiting'
}

export class CitizenService extends BaseService {
    async getCitizenBlockReasons(params: CitizensBlockReasonsPaginatedParameters): Promise<PaginatedResource<CitizenBlockReason>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/citizen-block-reasons${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCitizenBlockReasonById(id: number): Promise<CitizenBlockReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/citizen-block-reason/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createCitizenBlockReason(citizenBlockReasonData: Omit<BaseCitizenBlockReason, 'id'>) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/citizen-block-reason`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(citizenBlockReasonData)
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'InvalidValue') {
                    throw new InvalidValue();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async updateCitizenBlockReason(citizenBlockReasonData: BaseCitizenBlockReason): Promise<BaseCitizenBlockReason> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/citizen-block-reason/${citizenBlockReasonData.id}`, {
            method: 'PATCH',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify(citizenBlockReasonData)
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'InvalidValue') {
                    throw new InvalidValue();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async deleteCitizenBlockReason(id: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/citizen-block-reason/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status != 204) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'InvalidValue') {
                    throw new InvalidValue();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }
    }

    async getSimplifiedBlockReason(): Promise<CitizenDropdownResource[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/citizen/simplified-citizen-block-reasons`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }


    async getCitizens(params: CitizensPaginatedParameters): Promise<PaginatedResource<BaseCitizen>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizens${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCitizenById(id: number): Promise<Citizen> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createCitizen(citizenData: Omit<CitizenFormObject, 'facePictureImageUrl' | 'confirmPassword'>, citizenPhoto?: File) {
        const token = await this.getToken();

        const formData = new FormData();

        formData.append('citizenData', JSON.stringify(citizenData));
        if (citizenPhoto) {
            formData.append('file', citizenPhoto);
        }

        const res = await fetch(`${this.centralEndpoint}/citizen`, {
            method: 'POST',
            headers: this.getHeaders(token),
            body: formData
        });

        if (res.status != 201) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'AlreadyExists') {
                    throw new AlreadyExists();
                } else if (resJson.code === 'InvalidValue') {
                    throw new InvalidValue();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async toggleCitizenActivation(id: number, reason?: number) {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen/${id}/toggle-active`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ citizenBlockReasonId: reason })
        });

        if (res.status != 200) {
            if (res.status === 400) {
                const resJson = await res.json();
                if (resJson.code === 'NotFound') {
                    throw new NotFound();
                }
                throw new ClientError(resJson.code);
            }
            throw new Error();
        }

        return res.json();
    }

    async getCitizensLprDetectionsInterval(params: CitizensPaginatedParameters): Promise<IntervalResource<CitizenLprDetection>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen-lpr-detection/detections-interval${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCitizensLprDetections(params: CitizensPaginatedParameters): Promise<CitizenLprDetection[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen-lpr-detections${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCitizensLprCountDetections(params: CitizensPaginatedParameters): Promise<{ count: number; }> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen-lpr-detection/count${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getCitizensLprDetectionsById(id: number): Promise<CitizenLprDetection> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/citizen-lpr-detection/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getSimplifiedCitizen(textFilter: string): Promise<(DropdownResource & { document: string; })[]> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/simplified-citizens${this.encodeQueryParams({ textFilter })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

}

export const citizenService = new CitizenService();
